import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { Point, Projet, CategorieMarqueur } from '../../types/map';
import { ResultCard } from './ResultCard';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ResultsEmpty } from './ResultsEmpty';
import { resolveTextOnColor } from './textOnColor';
import './ResultsSheet.css';

export type SheetPosition = 'closed' | 'half' | 'full';

const ORDER: SheetPosition[] = ['closed', 'half', 'full'];
const DRAG_THRESHOLD = 8;  // px — seuil de déclenchement du drag (§4.2)
const FAST_VELOCITY = 0.5;  // px/ms — vélocité rapide
const LONG_DRAG = 120;      // px — distance minimale requise pour sauter 2 positions

export interface ResultsSheetProps {
  results: Projet[];
  categoriesById: Map<number, CategorieMarqueur>;
  position: SheetPosition;
  onPositionChange: (position: SheetPosition) => void;
  loading: boolean;
  /** Couleur du thème de la carte : fond du bas des cartes de résultat. */
  themeColor?: string;
  /** Clic picto d'une carte → recentre la carte (§5.4). */
  onPictoClick: (projet: Projet, point?: Point) => void;
  /** Clic « Afficher les détails » → ouvre le détail selon la catégorie. */
  onDetailsClick: (projet: Projet, point?: Point) => void;
}

/**
 * Bottom sheet « Vos résultats » (SPEC §4) : grille de cartes parcourables,
 * 3 positions (fermé/mi-ouvert/plein), drag souris + tactile, sans masquer la
 * cartographie. Composant CONTRÔLÉ — ne filtre pas les données.
 */
export const ResultsSheet: React.FC<ResultsSheetProps> = ({
  results,
  categoriesById,
  position,
  onPositionChange,
  loading,
  themeColor,
  onPictoClick,
  onDetailsClick,
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const dragState = useRef<{ startY: number; startTime: number; moved: boolean } | null>(null);
  const draggedRef = useRef(false);

  const nextPosition = useCallback((current: SheetPosition, steps: number): SheetPosition => {
    const idx = ORDER.indexOf(current);
    const clamped = Math.max(0, Math.min(ORDER.length - 1, idx + steps));
    return ORDER[clamped];
  }, []);

  const cycle = useCallback(() => {
    // fermé → mi-ouvert → plein → fermé (§4.1)
    const idx = ORDER.indexOf(position);
    onPositionChange(ORDER[(idx + 1) % ORDER.length]);
  }, [position, onPositionChange]);

  const handlePointerDown = (e: React.PointerEvent) => {
    dragState.current = { startY: e.clientY, startTime: e.timeStamp, moved: false };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const st = dragState.current;
    if (!st) return;
    const dy = e.clientY - st.startY;
    if (!st.moved && Math.abs(dy) < DRAG_THRESHOLD) return;
    st.moved = true;
    setDragOffset(Math.max(-300, Math.min(300, dy)));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const st = dragState.current;
    dragState.current = null;
    setDragOffset(0);
    if (!st || !st.moved) return;

    draggedRef.current = true; // empêche le onClick suivant
    const dy = e.clientY - st.startY;
    const elapsed = Math.max(1, e.timeStamp - st.startTime);
    const velocity = Math.abs(dy) / elapsed;
    // Sauter 2 positions uniquement si le geste est à la fois rapide ET long,
    // pour que la position 'half' reste toujours une étape atteignable (aimantation).
    const steps = velocity > FAST_VELOCITY && Math.abs(dy) > LONG_DRAG ? 2 : 1;
    // Drag vers le haut (dy < 0) → ouvrir ; vers le bas → fermer.
    const dir = dy < 0 ? steps : -steps;
    onPositionChange(nextPosition(position, dir));
  };

  const handleHeaderClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    cycle();
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      cycle();
    }
  };

  const open = position !== 'closed';
  const sheetStyle: Record<string, string> = {};
  if (dragOffset !== 0) sheetStyle.transform = `translateY(${dragOffset}px)`;
  if (themeColor) {
    sheetStyle['--result-theme'] = themeColor;
    const textOnTheme = resolveTextOnColor(themeColor);
    if (textOnTheme) sheetStyle['--result-theme-text'] = textOnTheme;
  }

  // W3.3 — Mémoïser la grille pour éviter les re-renders pendant le drag (dragOffset)
  // dragOffset est isolé dans un ref visuel qui ne déclenche pas de re-render de la liste
  const gridContent = useMemo(() => {
    if (loading) {
      return <ResultsSkeleton />;
    }
    if (results.length === 0) {
      return <ResultsEmpty />;
    }
    return (
      // Rôles explicites conservés : Safari retire la sémantique de liste
      // quand `list-style: none` est appliqué (§6.3).
      /* eslint-disable-next-line jsx-a11y/no-redundant-roles */
      <ul className="results-sheet__grid" role="list">
        {results.map((projet) => {
          const categorie =
            projet.categorieMarqueur ??
            (projet.categorieId != null ? categoriesById.get(projet.categorieId) : undefined);
          return (
            /* eslint-disable-next-line jsx-a11y/no-redundant-roles */
            <li key={projet.id} className="results-sheet__item" role="listitem">
              <ResultCard
                projet={projet}
                categorie={categorie}
                onPictoClick={onPictoClick}
                onDetailsClick={onDetailsClick}
              />
            </li>
          );
        })}
      </ul>
    );
  }, [results, categoriesById, loading, onPictoClick, onDetailsClick]);

  return (
    <section
      className={`results-sheet results-sheet--${position}${dragOffset !== 0 ? ' is-dragging' : ''}`}
      style={sheetStyle as React.CSSProperties}
      aria-label="Vos résultats"
    >
      <button
        type="button"
        id="results-sheet-header"
        className="results-sheet__header"
        aria-expanded={open}
        aria-controls="results-sheet-body"
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <span className="results-sheet__chevron" aria-hidden="true">
          ▲
        </span>
        <span>Vos résultats</span>
      </button>

      <div id="results-sheet-body" className="results-sheet__body">
        {/* Compteur annoncé aux lecteurs d'écran (§6.3) */}
        <p className="results-sheet__sr-count" aria-live="polite">
          {results.length} résultat{results.length > 1 ? 's' : ''}
        </p>

        {gridContent}
      </div>
    </section>
  );
};

export default ResultsSheet;
